import apiInstance from '@/api';
import toastMessage from '@/components/Toast';
import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import {
  BackgroundTask,
  RevalueType,
} from '@/pages/tools/RevaluationTool/types';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  TASK_POLL_LIMIT,
  isTaskActive,
  isTaskTerminal,
  revalueTypeLabel,
} from './constants';

export type PollBackgroundTasksResult = {
  tasks: BackgroundTask[];
  newlyCompletedIds: string[];
};

export type CancelResult = {
  /** Ids the backend accepted for cancellation — marked "cancelling" until a poll confirms terminal status. */
  cancelledIds: string[];
};

/**
 * Detects completions by diffing a fresh list against the previously known one:
 * any task that was active before and is terminal now just finished. Shared by
 * the stream ingest and the one-shot poll so both surface the same toasts/ring.
 */
const diffNewlyCompleted = (
  previousTasks: BackgroundTask[],
  tasks: BackgroundTask[],
): BackgroundTask[] => {
  const previouslyActiveIds = new Set(
    previousTasks.filter(isTaskActive).map((t) => t.task_id),
  );
  return tasks.filter(
    (t) => isTaskTerminal(t.status) && previouslyActiveIds.has(t.task_id),
  );
};

const notifyCompleted = (newlyCompleted: BackgroundTask[]) => {
  newlyCompleted.forEach((task) => {
    const label = revalueTypeLabel(task.revalue_type);
    if (task.statistic?.error) {
      toastMessage.error(
        `${label} revaluation failed: ${task.statistic.error}`,
      );
    } else {
      toastMessage.success(`${label} revaluation finished.`);
    }
  });
};

/**
 * Ingests a full task-list snapshot pushed by the SSE stream. Same shape and
 * same completion-diffing as a poll, but the list is delivered by the caller
 * (a stream frame) rather than fetched — this is what keeps the bell live now
 * that we no longer poll on an interval. See useBackgroundTasksStream.
 */
export const ingestStreamedTasks = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'backgroundTasks/ingestStream',
  async (
    tasks: BackgroundTask[],
    { getState },
  ): Promise<PollBackgroundTasksResult> => {
    const previousTasks = getState().backgroundTasks.tasks;
    const newlyCompleted = diffNewlyCompleted(previousTasks, tasks);
    notifyCompleted(newlyCompleted);
    return { tasks, newlyCompletedIds: newlyCompleted.map((t) => t.task_id) };
  },
);

/**
 * One-shot fetch of the background-task listing (`active=false` so it includes
 * finished history). Not dispatched automatically anywhere — the SSE stream
 * (useBackgroundTasksStream) is the sole live source. Kept only for an optional
 * on-demand refresh (e.g. a manual "refresh" button).
 */
export const pollBackgroundTasks = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'backgroundTasks/poll',
  async (_, { getState }): Promise<PollBackgroundTasksResult> => {
    const response = await apiInstance.revalueTrades.listTasks({
      active: false,
      limit: TASK_POLL_LIMIT,
    });
    const tasks: BackgroundTask[] = response.data?.data ?? [];

    const previousTasks = getState().backgroundTasks.tasks;
    const newlyCompleted = diffNewlyCompleted(previousTasks, tasks);
    notifyCompleted(newlyCompleted);

    return {
      tasks,
      newlyCompletedIds: newlyCompleted.map((t) => t.task_id),
    };
  },
  {
    // Skip overlapping polls (e.g. a manual refresh while the fallback poll runs).
    condition: (_, { getState }) => !getState().backgroundTasks.loading,
  },
);

/** Cooperatively cancels a single run; it stops at its next trade boundary. */
export const cancelBackgroundTask =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'backgroundTasks/cancelOne',
    async (taskId: string): Promise<CancelResult> => {
      try {
        await apiInstance.revalueTrades.cancelTask(taskId);
        toastMessage.success('Revaluation cancellation requested.');
        // No refetch — the SSE stream pushes the updated list on cancellation.
        return { cancelledIds: [taskId] };
      } catch (error: any) {
        toastMessage.error(
          `Failed to cancel revaluation: ${getThunkError(error)}`,
        );
        return { cancelledIds: [] };
      }
    },
  );

/**
 * Cancels all of the current user's queued + running runs (optionally scoped to
 * one revalue type). The SSE stream reflects the resulting status changes.
 */
export const cancelAllBackgroundTasks =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'backgroundTasks/cancelAll',
    async (
      revalueType: RevalueType | undefined,
      { getState },
    ): Promise<CancelResult> => {
      try {
        const response = await apiInstance.revalueTrades.cancelAllTasks(
          revalueType ? { revalue_type: revalueType } : {},
        );
        const count = response.data?.data?.cancelled_count ?? 0;
        // Prefer the ids the backend reports; fall back to the active ids we
        // know about so the UI still updates optimistically.
        const cancelledIds: string[] =
          response.data?.data?.cancelled_task_ids ??
          getState()
            .backgroundTasks.tasks.filter(isTaskActive)
            .map((t) => t.task_id);
        toastMessage.success(
          count > 0
            ? `Cancellation requested for ${count} task${count === 1 ? '' : 's'}.`
            : 'No running tasks to cancel.',
        );
        // No refetch — the SSE stream pushes the updated list on cancellation.
        return { cancelledIds };
      } catch (error: any) {
        toastMessage.error(
          `Failed to cancel revaluations: ${getThunkError(error)}`,
        );
        return { cancelledIds: [] };
      }
    },
  );
